// src/components/pages/EventManager/organisms/EventsTable.js

import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy as firestoreOrderBy,
  onSnapshot,
} from "firebase/firestore";
import { db, auth } from "../../../../firebase";

import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  TableSortLabel,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Link } from "react-router-dom";

const EventsTable = ({ theme, onRequestDelete, onRequestEdit }) => {
  const [events, setEvents] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("createdAt");

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const eventsRef = collection(db, "events");
    const q = query(
      eventsRef,
      where("creatorUid", "==", currentUser.uid),
      firestoreOrderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventsData = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setEvents(eventsData);
    });

    return () => unsubscribe();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  function normalizeForCompare(val) {
    if (val?.toDate) return val.toDate();
    return val;
  }

  function descendingComparator(a, b, key) {
    const aVal = normalizeForCompare(a[key]);
    const bVal = normalizeForCompare(b[key]);
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  }

  function getComparator(ord, key) {
    return ord === "desc"
      ? (a, b) => descendingComparator(b, a, key)
      : (a, b) => descendingComparator(a, b, key);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderVal = comparator(a[0], b[0]);
      if (orderVal !== 0) return orderVal;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const sortedEvents = stableSort(events, getComparator(order, orderBy));

  const SortableHeader = ({ col }) => (
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? order : "asc"}
      onClick={() => handleRequestSort(col)}
      sx={{
        color: theme.colors.amber,
        "&.Mui-active": { color: theme.colors.white },
        "& .MuiTableSortLabel-icon": { color: theme.colors.amber },
        "&.Mui-active .MuiTableSortLabel-icon": { color: theme.colors.white },
        "&:hover": {
          color: theme.colors.green,
          "& .MuiTableSortLabel-icon": { color: theme.colors.green },
        },
      }}
    >
      {col.charAt(0).toUpperCase() + col.slice(1)}
    </TableSortLabel>
  );

  const NonSortableHeader = ({ label }) => (
    <span
      style={{
        color: theme.colors.amber,
        fontFamily: "basic-sans",
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );

  return (
    <TableContainer
      component={Paper}
      sx={{
        marginTop: 4,
        fontFamily: "basic-sans",
        backgroundColor: theme.colors.mediumPurple,
        color: "white",
      }}
    >
      {events.length === 0 ? (
        <Typography variant="body1" sx={{ padding: 2, color: "white" }}>
          No events found.
        </Typography>
      ) : (
        <Table>
          <TableHead
            sx={{
              backgroundColor: theme.colors.headerPurple,
              "& th": {
                color: theme.colors.amber,
                fontFamily: "basic-sans",
                fontWeight: 700,
                borderBottom: `1px solid ${theme.colors.palePurple}`,
              },
            }}
          >
            <TableRow>
              {[
                "title",
                "description",
                "startDate",
                "endDate",
                "price",
                "createdAt",
              ].map((col) => (
                <TableCell key={col}>
                  <SortableHeader col={col} />
                </TableCell>
              ))}

              {/* Optional: show rentalDurationDays if you want it visible in the table */}
              <TableCell>
                <NonSortableHeader label="Rental Days" />
              </TableCell>

              <TableCell>
                <NonSortableHeader label="Content" />
              </TableCell>

              <TableCell align="right">
                <NonSortableHeader label="Actions" />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              backgroundColor: theme.colors.mediumPurple,
              "& td": {
                color: "white",
                fontFamily: "basic-sans",
                fontWeight: 300,
                borderBottom: `1px solid ${theme.colors.palePurple}`,
              },
            }}
          >
            {sortedEvents.map((event) => (
              <TableRow key={event.id}>
                <TableCell>{event.title}</TableCell>
                <TableCell>{event.description}</TableCell>

                <TableCell>
                  {event.startDate?.toDate
                    ? event.startDate.toDate().toLocaleDateString()
                    : new Date(event.startDate).toLocaleDateString()}
                </TableCell>

                <TableCell>
                  {event.endDate?.toDate
                    ? event.endDate.toDate().toLocaleDateString()
                    : new Date(event.endDate).toLocaleDateString()}
                </TableCell>

                <TableCell>{event.price}</TableCell>

                <TableCell>
                  {event.createdAt?.toDate
                    ? event.createdAt.toDate().toLocaleDateString()
                    : new Date(event.createdAt).toLocaleDateString()}
                </TableCell>

                <TableCell>
                  {Number.isFinite(event.rentalDurationDays)
                    ? `${event.rentalDurationDays}`
                    : "-"}
                </TableCell>

                <TableCell>
                  <Link
                    to="/challenge/admin/events/content"
                    state={{ event }}
                    style={{
                      color: theme.colors.amber,
                      textDecoration: "none",
                    }}
                  >
                    Edit &amp; Add Content
                  </Link>
                </TableCell>

                <TableCell align="right">
                  <Tooltip title="Edit">
                    <span>
                      <IconButton
                        aria-label="edit"
                        size="small"
                        onClick={() => onRequestEdit?.(event)}
                        disabled={!onRequestEdit}
                      >
                        <EditOutlinedIcon sx={{ color: theme.colors.amber }} />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        aria-label="delete"
                        size="small"
                        color="error"
                        onClick={() => onRequestDelete?.(event.id, event.title)}
                        disabled={!onRequestDelete}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};

export default EventsTable;
